const BASE = "/api";

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string) {
    super(code);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; recoveryToken?: string } = {}
): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (options.recoveryToken) headers["X-Recovery-Token"] = options.recoveryToken;

  const res = await fetch(`${BASE}${path}`, {
    method: options.method ?? (options.body ? "POST" : "GET"),
    headers,
    credentials: "include",
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError(res.status, (data as { error?: string }).error ?? "unknown_error");
  }
  return data as T;
}

// --- Публичный клиентский API ---
export const clientApi = {
  createVisitor: (utm: Record<string, string | undefined>) =>
    request<{ visitorId: string; shortId: string; status: string }>("/visitor", { body: utm }),
  heartbeat: (visitorId: string) => request(`/visitor/${visitorId}/heartbeat`, { body: {} }),
  selectPackage: (visitorId: string, packageId: string) =>
    request(`/visitor/${visitorId}/select-package`, { body: { packageId } }),
  listPackages: () => request<{ packages: any[] }>("/packages"),
  siteStatus: () => request<{ maintenanceMode: boolean; workingHoursText: string; whatsappUrl: string }>("/site-status"),
  createOrder: (input: { visitorId: string; packageId: string; phone: string }) =>
    request<{ order: any; recoveryToken: string; kaspiUrlTemplate: string | null; whatsappUrl: string | null }>(
      "/orders",
      { body: input }
    ),
  getOrder: (id: string, recoveryToken: string) => request<{ order: any }>(`/orders/${id}`, { recoveryToken }),
  returnedFromPayment: (id: string, recoveryToken: string) =>
    request<{ order: any }>(`/orders/${id}/returned-from-payment`, { body: {}, recoveryToken }),
  retryPayment: (id: string, recoveryToken: string) =>
    request<{ order: any }>(`/orders/${id}/retry-payment`, { body: {}, recoveryToken }),
  requestNewCode: (id: string, recoveryToken: string) =>
    request<{ order: any }>(`/orders/${id}/request-new-code`, { body: {}, recoveryToken }),
  submitCode: (id: string, code: string, recoveryToken: string) =>
    request<{ order: any }>(`/orders/${id}/code`, { body: { code }, recoveryToken }),
};

// --- Admin API (owner + operator) ---
export const adminApi = {
  login: (email: string, password: string) => request<{ user: any }>("/auth/login", { body: { email, password } }),
  logout: () => request("/auth/logout", { body: {} }),
  me: () => request<{ user: any }>("/auth/me"),
  bootstrapOwner: (input: { token: string; name: string; email: string; password: string }) =>
    request("/auth/bootstrap-owner", { body: input }),

  setPresence: (presence: "online" | "offline") => request("/admin/presence", { body: { presence } }),
  listOrders: (statuses?: string[]) =>
    request<{ orders: any[] }>(`/admin/orders${statuses ? `?statuses=${statuses.join(",")}` : ""}`),
  myOrders: () => request<{ orders: any[] }>("/admin/orders/mine"),
  nextOrder: () => request<{ order: any | null }>("/admin/orders/next", { body: {} }),
  problemOrders: () => request<{ orders: any[] }>("/admin/orders/problems"),
  getOrder: (id: string) => request<{ order: any }>(`/admin/orders/${id}`),
  auditLog: (id: string) => request<{ events: any[] }>(`/admin/orders/${id}/audit`),
  reassign: (id: string, operatorId: string | null) =>
    request(`/admin/orders/${id}/reassign`, { body: { operatorId } }),

  confirmPayment: (id: string) => request<{ order: any }>(`/admin/orders/${id}/payment/confirm`, { body: {} }),
  paymentNotFound: (id: string) => request<{ order: any }>(`/admin/orders/${id}/payment/not-found`, { body: {} }),
  initiateCode: (id: string) => request<{ order: any }>(`/admin/orders/${id}/code/initiate`, { body: {} }),
  getCode: (id: string) => request<{ code: string; attempts: number; maxAttempts: number; expiresAt: string }>(`/admin/orders/${id}/code`),
  codeResult: (id: string, result: "valid" | "invalid" | "impossible") =>
    request<{ order: any }>(`/admin/orders/${id}/code/result`, { body: { result } }),
  codeResend: (id: string) => request<{ order: any }>(`/admin/orders/${id}/code/resend`, { body: {} }),
  escalate: (id: string) => request<{ order: any }>(`/admin/orders/${id}/escalate`, { body: {} }),

  refundProcess: (id: string) => request<{ order: any }>(`/admin/orders/${id}/refund/process`, { body: {} }),
  refundComplete: (id: string) => request<{ order: any }>(`/admin/orders/${id}/refund/complete`, { body: {} }),

  listOperators: () => request<{ operators: any[] }>("/admin/operators"),
  createOperator: (input: { name: string; email: string; password: string }) =>
    request<{ user: any }>("/admin/operators", { body: input }),
  setOperatorStatus: (id: string, status: "active" | "disabled") =>
    request(`/admin/operators/${id}/status`, { body: { status } }),

  listAllPackages: () => request<{ packages: any[] }>("/admin/packages"),
  upsertPackage: (pkg: any) => request<{ package: any }>("/admin/packages", { body: pkg }),

  getSettings: () =>
    request<{ kaspiPaymentUrlTemplate: string; whatsappUrl: string; maintenanceMode: boolean; workingHoursText: string }>("/admin/settings"),
  updateSettings: (s: { kaspiPaymentUrlTemplate?: string; whatsappUrl?: string; maintenanceMode?: boolean; workingHoursText?: string }) =>
    request("/admin/settings", { body: s }),

  orderHistory: () => request<{ orders: any[] }>("/admin/orders/history"),

  funnel: (since?: string) => request<{ visitors: any[]; orders: any[]; revenue: number }>(`/admin/analytics/funnel${since ? `?since=${since}` : ""}`),
};

export { request };